/**
 * Sentry beforeSend filter — extracted from instrumentation-client.ts so the
 * filtering logic is unit-testable without booting the Sentry SDK.
 *
 * Returning `null` drops the event. Returning the event (mutated for PII)
 * forwards it to Sentry. Filter rules and rationale documented in
 * locarora-admin/docs/SENTRY_TRIAGE_2026_05_02.md.
 *
 * NOTE on stack-frame filenames: `beforeSend` runs in the browser BEFORE Sentry
 * applies source maps. Frame filenames at this point are raw bundle URLs
 * (e.g. `https://locarora.com/_next/static/chunks/main-app-abc.js`) or `app:///`
 * prefixed paths — never source paths like `node_modules/next/src/...`. Any
 * filter that tries to substring-match a source path will silently never fire.
 * Match against `_next/`, `@sentry/`, full URLs, or function names instead.
 */

import { isHardNavigationPending } from "@/lib/navigation/hard-navigate";
import { NETWORK_ABORT_MESSAGES } from "@/lib/sentry/network-abort";

interface MinimalStackFrame {
  filename?: string;
}

interface MinimalException {
  value?: string;
  mechanism?: { handled?: boolean; type?: string };
  stacktrace?: { frames?: MinimalStackFrame[] };
}

export type SentryLevel =
  | "fatal"
  | "error"
  | "warning"
  | "log"
  | "info"
  | "debug";

// Mirrors Sentry's `Primitive` to keep beforeSendFilter compatible with the
// ErrorEvent type without importing the SDK in this unit-tested module.
type SentryPrimitive =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

export interface SentryEventLike {
  exception?: { values?: MinimalException[] };
  request?: { data?: unknown; cookies?: unknown };
  tags?: Record<string, SentryPrimitive>;
  level?: SentryLevel;
  environment?: string;
}

export function beforeSendFilter<E extends SentryEventLike>(event: E): E | null {
  const exception = event.exception?.values?.[0];
  const msg = exception?.value || "";
  const frames = exception?.stacktrace?.frames ?? [];

  // Never let local development noise pollute production error tracking. The DSN
  // is already gated to NODE_ENV==="production", but a misconfigured local env
  // (or a stale build) leaked HMR errors like "Router action dispatched before
  // initialization" tagged environment:development (LOCARORA-WEB-W, 27/7d).
  // Belt-and-suspenders: drop anything explicitly tagged as the dev environment.
  if (event.environment === "development") {
    return null;
  }

  if (msg.includes("NEXT_NOT_FOUND") || msg.includes("NEXT_REDIRECT")) {
    return null;
  }

  const appFrames = frames.filter((f) => f.filename?.startsWith("app:///"));
  if (
    appFrames.length > 0 &&
    appFrames.every((f) => /^app:\/\/\/[a-z-]+$/.test(f.filename ?? ""))
  ) {
    return null;
  }

  // Drop unhandled network-fetch rejections that bubbled to the global
  // `unhandledrejection` handler. These are caused by mobile Safari/Chrome
  // WebView (or in-app browsers) suspending in-flight fetches when the page
  // is backgrounded — Next.js' Server Action transport, RSC prefetch, and
  // other framework fetches reject and reach Sentry as noise.
  //
  // Browser variants: Chromium "Failed to fetch", Safari "Load failed",
  // Firefox "NetworkError when attempting to fetch resource.", generic
  // "network error".
  //
  // Trade-off: we don't disambiguate user-code rejections from framework
  // ones via stack frames, because at `beforeSend` time the bundle URLs
  // are identical for both (e.g. `app:///_next/static/chunks/...`). The
  // (msg + mechanism) tuple is the only reliable signal in production.
  // We accept that an unhandled rejection from our own missing-try/catch
  // would also be dropped — known call sites are explicitly try/catched
  // (BottomNavBar, UserMenu, usePushNotifications, useSupportConversation,
  // ProductDetailClient). Future occurrences should surface via dev
  // console.warn first; if a real bug needs Sentry visibility, wrap it in
  // try/catch and `Sentry.captureException` so `handled: true` keeps it.
  // mechanism.type: 구식 SDK는 "onunhandledrejection", 신식은
  // "auto.browser.global_handlers.onunhandledrejection". strict === 비교는 신식을
  // 놓쳐 WEB-E/G/T가 필터를 통과했다 — endsWith로 두 형식 모두 포섭한다.
  if (NETWORK_ABORT_MESSAGES.includes(msg)) {
    // (a) Unhandled rejection variant — mobile background fetch aborts that
    //     bubble to the global onunhandledrejection handler.
    const isUnhandledRejection =
      exception?.mechanism?.handled === false &&
      (exception?.mechanism?.type?.endsWith("onunhandledrejection") ?? false);

    // (b) Stack-less transport abort — a network TypeError with ZERO frames is
    //     unactionable (you can't locate where it came from) and is the
    //     signature of framework Server-Action / RSC transport aborts on mobile
    //     Safari, which arrive `handled: true` with no app frame
    //     (LOCARORA-WEB-E on /verify-phone, 418 events, 0 users). Deliberate
    //     Sentry.captureException call sites always carry app frames, so this
    //     does NOT drop intentional captures — only the unlocatable transport
    //     noise the (a) branch misses because it's marked handled.
    const isStacklessTransportAbort = frames.length === 0;

    // (c) The document is being torn down by a deliberate full-page navigation
    //     (`hardNavigate()`), which aborts whatever Next.js still had in
    //     flight — most often the router-tree refresh that a Server Action
    //     kicks off after it changes the auth cookie. React 19 re-throws that
    //     abort through `reportError()`, so it arrives via `window.onerror`
    //     with ONE minified chunk frame: `handled: false` but not a rejection,
    //     and not stack-less, which is how LOCARORA-WEB-E slipped past both
    //     branches above for six months.
    //
    //     Gating on the flag rather than on the message keeps this narrow: a
    //     genuine `Load failed` outside a teardown window still reports.
    const isDocumentTeardown = isHardNavigationPending();

    if (isUnhandledRejection || isStacklessTransportAbort || isDocumentTeardown) {
      return null;
    }
  }

  if (
    msg === "Maximum call stack size exceeded." &&
    frames.length <= 1 &&
    !frames.some((f) => f.filename && f.filename !== "undefined")
  ) {
    return null;
  }

  // Same error from a script injected into the page — not ours. The stack is
  // a minified mutual recursion whose every frame is the document URL itself
  // (an inline script, e.g. app:///ko/profile/reservations/<id>), with no
  // frame from our bundle. LOCARORA-WEB-6S: iOS Google app (GSA) translating a
  // Korean page for a Japanese-locale device. Our own inline scripts (install
  // banner, gtag bootstrap) are short and non-recursive, so recursion that
  // never touches /_next/ cannot be our code; recursion in our code always
  // carries /_next/ chunk frames and is kept.
  if (
    msg === "Maximum call stack size exceeded." &&
    frames.length > 1 &&
    !frames.some((f) => f.filename?.includes("/_next/"))
  ) {
    return null;
  }

  // Same injected-script family, different symptom: a rejection whose message
  // is a bare minified identifier ("La") and whose every frame is a document
  // URL (app:///zh-TW/s/kaohsiung), never a /_next/ chunk. All four of
  // LOCARORA-WEB-73..76 came from the iOS Google app / iOS Chrome, whose
  // translate script runs inline in the page. Our errors always carry a real
  // message and a bundle frame, so the narrow shape below cannot be ours.
  if (
    /^[A-Za-z_$]{1,3}$/.test(msg) &&
    frames.length > 0 &&
    frames.every((f) => f.filename?.startsWith("app:///")) &&
    !frames.some((f) => f.filename?.includes("/_next/"))
  ) {
    return null;
  }

  // Not a browser at all: a script runtime that fetched our page and evaluated
  // the bundle text itself. Every frame is the anonymous "<script>" name or
  // Deno's own "ext:" internals — a real browser always names a chunk URL
  // (https://… or app:///…), and Chrome names inline scripts after the document
  // URL. Its stubbed Web APIs then break (navigator.serviceWorker without
  // getRegistration, a fetch Response with no body stream). LOCARORA-WEB-79/7A:
  // one visit, "Chrome 145" with an empty user agent, the only two events of
  // this shape in 90 days.
  if (
    frames.length > 0 &&
    frames.every((f) => f.filename === "<script>" || f.filename?.startsWith("ext:"))
  ) {
    return null;
  }

  // Browser auto-translate (Chrome/Safari built-in translate, Google Translate
  // extension) swaps text nodes under React's feet. During the next commit React
  // can no longer find its own DOM nodes and throws removeChild/insertBefore
  // "not a child" or "The object can not be found here." (DOMException 8). The
  // stack is pure react-dom commit phase with no app frame. We can't fix this
  // client-side — and must NOT disable translation (international rental app) —
  // so drop it as known third-party noise. Error boundaries already show a
  // fallback, so UX is unaffected. LOCARORA-WEB-1V/10/3X/1F.
  const browserMutationNoise = [
    "Failed to execute 'removeChild' on 'Node'",
    "Failed to execute 'insertBefore' on 'Node'",
    "The object can not be found here.",
  ];
  if (browserMutationNoise.some((m) => msg.includes(m))) {
    return null;
  }

  // The whole Google Maps JS API failing to load (vis.gl react-google-maps
  // loader onerror) — almost always the network/region blocking
  // maps.googleapis.com (e.g. China's firewall) or a transient CDN failure.
  // External, caught by the api-provider error boundary, not our bug.
  // LOCARORA-WEB-44.
  if (msg.includes("The Google Maps JavaScript API could not load")) {
    return null;
  }

  // Google Maps' WebGL vector renderer throws "EpochMismatch,<n>,<m>" when a
  // tile draw lands after the map has moved on to a newer render epoch. It is
  // internal to maps-api-v3 and recovers on the next frame. One bundle frame
  // (the wrapper that scheduled the callback) sits at the bottom of the stack,
  // so the maps-only tagging below never matches it. LOCARORA-WEB-7E.
  if (
    msg.includes("EpochMismatch") &&
    frames.some((f) => f.filename?.includes("maps-api-v3"))
  ) {
    return null;
  }

  // Google Maps JS API의 sub-module 동적 로드 실패(예: "Could not load \"util\""):
  // 우리 코드가 직접 throw한 것이 아니라 외부 스크립트의 일시적 네트워크 이슈.
  // drop하지 않고 낮은 severity로 모니터링하여 빈도 추이만 추적.
  const isExternalGoogleMapsOnly =
    frames.length > 0 &&
    frames.every(
      (f) =>
        f.filename?.includes("maps-api-v3") ||
        f.filename?.includes("maps.googleapis.com"),
    );
  if (isExternalGoogleMapsOnly) {
    event.tags = {
      ...(event.tags ?? {}),
      source: "external_script",
      library: "google-maps",
    };
    event.level = "info";
  }

  if (event.exception?.values) {
    event.exception.values.forEach((v) => {
      if (v.value) {
        v.value = v.value.replace(/[\w.-]+@[\w.-]+\.\w+/g, "[EMAIL]");
      }
    });
  }

  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
  }

  return event;
}
